import React, { useState } from 'react';

/**
 * Kat Karşılığı iş akışı: 7 adımlık sıra, adım çubuğu ve her adımın başında gösterilen
 * yardım paneli (ne yapılacağı, kontrol listesi, uyarılar, önceki/sonraki adım).
 */

export type AdimKey = 'genel' | 'taraflar' | 'bloklar' | 'bolumler' | 'yerlesim' | 'paylasim' | 'kontrol';

export interface AdimTanimi {
    key: AdimKey;
    no: number;
    kisaAd: string;
    baslik: string;
    ikon: string;
    aciklama: string;
    ipuclari: string[];
}

export const ADIMLAR: AdimTanimi[] = [
    {
        key: 'genel', no: 1, kisaAd: 'Künye', ikon: 'fa-file-signature',
        baslik: 'Proje ve Arsa Künyesinin Oluşturulması',
        aciklama: 'Arsanın tapu ve konum bilgilerini (takip no, ada, parsel, il, ilçe, mahalle) girin. Bu bilgiler raporlarda ve sözleşmelerde kullanılır.',
        ipuclari: [
            'Arsa m² elle girilmez: Adım 2\'de tarafların hisse m²\'lerinin toplamından otomatik hesaplanır.',
            'Toplam değer de elle girilmez: Adım 4\'te bölüm fiyatlarının toplamından hesaplanır.',
        ],
    },
    {
        key: 'taraflar', no: 2, kisaAd: 'Taraflar', ikon: 'fa-users',
        baslik: 'Tarafların ve Hak Ediş Oranlarının Tanımlanması',
        aciklama: 'Arsa ortaklarını ve müteahhit(ler)i hisse m²\'leriyle ekleyin. Müteahhit için sabit bir pay yüzdesi de girilebilir; kalan yüzde ortaklara hisse m²\'lerine göre dağıtılır.',
        ipuclari: [
            'Hisse oranı ve hak edilen pay (TL) sistem tarafından hesaplanır; yalnızca hisse m² girmeniz yeterli.',
            'Çok sayıda ortak varsa "Şablon İndir / Excel\'den Yükle" ile toplu girebilirsiniz.',
        ],
    },
    {
        key: 'bloklar', no: 3, kisaAd: 'Bloklar & Katlar', ikon: 'fa-building',
        baslik: 'Mimari Altyapının Kurulması',
        aciklama: 'Blokları ve her bloğun katlarını oluşturun; kat ebatlarını (en × boy) girin ve istenirse kat planını çizim araçlarıyla çizin.',
        ipuclari: [
            'Blok için girdiğiniz kat sayısı ile eklenen kat adedi eşleşmezse uyarı gösterilir.',
            'Kat planını çizmek için kat satırındaki konum ikonuna tıklayın. Bölümlerin yerleşimi Adım 5\'te yapılır.',
        ],
    },
    {
        key: 'bolumler', no: 4, kisaAd: 'Bölümler', ikon: 'fa-th-large',
        baslik: 'Bağımsız Bölümlerin Eklenmesi ve Fiyatlandırılması',
        aciklama: 'Daire, dükkan gibi bağımsız bölümleri blok/kat/cephe bilgileriyle ekleyin ve m² ile fiyatlarını girin. Bölüm fiyatlarının toplamı arsanın toplam değerini oluşturur.',
        ipuclari: [
            'Tek tek "Bölüm Ekle", kat kat "Toplu Ekle" ya da "Excel\'den Yükle" kullanabilirsiniz (İşlemler menüsü).',
            'Cepheleri "Cepheler" butonundan tanımlayın; bölümlere cephe atanabilir.',
        ],
    },
    {
        key: 'yerlesim', no: 5, kisaAd: 'Yerleşim', ikon: 'fa-map-marked-alt',
        baslik: 'Bölümlerin Kat Planı ve Görünümlerde Yerleştirilmesi',
        aciklama: 'Kat planında her bölümün etiketini doğru yerine sürükleyin. Ayrıca "Görünümler" ile bina fotoğrafı üzerine bölümleri sürükle-bırak ile yerleştirin.',
        ipuclari: [
            'Henüz yerleştirilmemiş bölümler kat planının sol üst köşesinde üst üste görünür.',
            'Yerleşim, Adım 6\'daki vaziyet ve görünüm ekranlarında kullanılır.',
        ],
    },
    {
        key: 'paylasim', no: 6, kisaAd: 'Daire Atama', ikon: 'fa-hand-pointer',
        baslik: 'Hak Sahiplerine Daire Atamalarının Yapılması',
        aciklama: 'Sağdaki panelden ortağı ya da müteahhidi sürükleyip bir bölümün üzerine bırakın; açılan pencerede paylaşım oranını girin. Tablo, vaziyet ya da görünüm ekranlarından birini kullanabilirsiniz.',
        ipuclari: [
            'Paneldeki tutar, kişinin henüz almadığı kalan payıdır. Bölüm fiyatı kalan payı aşıyorsa o bölüm soluk görünür ve bırakılamaz.',
            '"Dağıtım Önerisi" hak edişlere göre otomatik bir atama önerisi hazırlar.',
        ],
    },
    {
        key: 'kontrol', no: 7, kisaAd: 'Kontrol', ikon: 'fa-clipboard-check',
        baslik: 'Finansal Kontrol ve Kesinleştirme',
        aciklama: 'Her tarafın hak ettiği pay ile aldığı bölümlerin değerini karşılaştırın, eksik ya da fazla dağıtımları düzeltin ve raporları alın.',
        ipuclari: [
            'Tüm kontroller yeşil olduğunda dağıtım kesinleşmeye hazırdır; raporları "İndir" menüsünden alın.',
        ],
    },
];

export type KontrolDurumu = 'tamam' | 'uyari' | 'hata' | 'bilgi';
export interface KontrolMaddesi {
    durum: KontrolDurumu;
    metin: string;
    eylem?: { etiket: string; onClick: () => void };
}

const DURUM_STIL: Record<KontrolDurumu, { ikon: string; renk: string; zemin: string }> = {
    tamam: { ikon: 'fa-check-circle', renk: '#16a34a', zemin: '#f0fdf4' },
    uyari: { ikon: 'fa-exclamation-triangle', renk: '#d97706', zemin: '#fffbeb' },
    hata: { ikon: 'fa-times-circle', renk: '#dc2626', zemin: '#fef2f2' },
    bilgi: { ikon: 'fa-info-circle', renk: '#2563eb', zemin: '#eff6ff' },
};

/** Bir adımın genel durumu: hata/uyarı varsa onu, yoksa (en az bir tamam varsa) tamam. */
export const adimDurumu = (maddeler: KontrolMaddesi[]): 'tamam' | 'uyari' | 'bos' => {
    if (maddeler.some(m => m.durum === 'hata' || m.durum === 'uyari')) return 'uyari';
    return maddeler.some(m => m.durum === 'tamam') ? 'tamam' : 'bos';
};

// ── Adım çubuğu ──────────────────────────────────────────────────────────────

export const AdimCubugu: React.FC<{
    aktif: AdimKey;
    durumlar: Record<AdimKey, 'tamam' | 'uyari' | 'bos'>;
    etiketEki?: Partial<Record<AdimKey, string>>;
    onSec: (k: AdimKey) => void;
}> = ({ aktif, durumlar, etiketEki, onSec }) => (
    <div className="kka-adim-cubugu">
        {ADIMLAR.map((a, i) => {
            const d = durumlar[a.key];
            const secili = a.key === aktif;
            return (
                <React.Fragment key={a.key}>
                    {i > 0 && <div className={`kka-adim-cizgi${durumlar[ADIMLAR[i - 1].key] === 'tamam' ? ' tamam' : ''}`} />}
                    <button className={`kka-adim${secili ? ' aktif' : ''} ${d}`} onClick={() => onSec(a.key)}
                        title={`Adım ${a.no}: ${a.baslik}${d === 'uyari' ? ' — dikkat edilmesi gereken noktalar var' : d === 'tamam' ? ' — tamamlandı' : ''}`}>
                        <span className="kka-adim-no">
                            {d === 'tamam' && !secili ? <i className="fas fa-check"></i> : d === 'uyari' && !secili ? '!' : a.no}
                        </span>
                        <span className="kka-adim-ad">
                            {a.kisaAd}
                            {etiketEki?.[a.key] && <span className="kka-adim-ek">{etiketEki[a.key]}</span>}
                        </span>
                    </button>
                </React.Fragment>
            );
        })}
    </div>
);

// ── Adım yardım paneli ───────────────────────────────────────────────────────

const GIZLI_ANAHTAR = 'kka_rehber_gizli';
const gizliOku = (): boolean => { try { return localStorage.getItem(GIZLI_ANAHTAR) === '1'; } catch { return false; } };

export const AdimRehberi: React.FC<{
    adim: AdimKey;
    maddeler: KontrolMaddesi[];
    onGit: (k: AdimKey) => void;
}> = ({ adim, maddeler, onGit }) => {
    const [gizli, setGizli] = useState<boolean>(gizliOku);
    const idx = ADIMLAR.findIndex(a => a.key === adim);
    const a = ADIMLAR[idx];
    const onceki = ADIMLAR[idx - 1];
    const sonraki = ADIMLAR[idx + 1];
    const sorunlu = maddeler.filter(m => m.durum === 'hata' || m.durum === 'uyari').length;

    const gizle = (v: boolean) => {
        setGizli(v);
        try { localStorage.setItem(GIZLI_ANAHTAR, v ? '1' : '0'); } catch { /* yok say */ }
    };

    return (
        <div className="kka-rehber">
            <div className="kka-rehber-baslik">
                <span className="kka-rehber-no">{a.no}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="kka-rehber-ust">Adım {a.no} / {ADIMLAR.length}</div>
                    <div className="kka-rehber-ad"><i className={`fas ${a.ikon}`}></i> {a.baslik}</div>
                </div>
                {sorunlu > 0 && (
                    <span className="kka-rehber-rozet uyari"><i className="fas fa-exclamation-triangle"></i> {sorunlu} uyarı</span>
                )}
                {sorunlu === 0 && maddeler.some(m => m.durum === 'tamam') && (
                    <span className="kka-rehber-rozet tamam"><i className="fas fa-check"></i> Hazır</span>
                )}
                <button className="kka-rehber-gizle" onClick={() => gizle(!gizli)} title={gizli ? 'Yardımı göster' : 'Yardımı gizle'}>
                    <i className={`fas ${gizli ? 'fa-question-circle' : 'fa-chevron-up'}`}></i> {gizli ? 'Yardım' : 'Gizle'}
                </button>
            </div>

            {!gizli && (
                <div className="kka-rehber-govde">
                    <p className="kka-rehber-aciklama">{a.aciklama}</p>
                    {a.ipuclari.length > 0 && (
                        <ul className="kka-rehber-ipucu">
                            {a.ipuclari.map((ip, i) => <li key={i}><i className="fas fa-lightbulb"></i>{ip}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {maddeler.length > 0 && (!gizli || sorunlu > 0) && (
                <div className="kka-rehber-kontroller">
                    {maddeler.filter(m => !gizli || m.durum === 'hata' || m.durum === 'uyari').map((m, i) => {
                        const st = DURUM_STIL[m.durum];
                        return (
                            <div key={i} className="kka-rehber-madde" style={{ background: st.zemin }}>
                                <i className={`fas ${st.ikon}`} style={{ color: st.renk }}></i>
                                <span style={{ flex: 1 }}>{m.metin}</span>
                                {m.eylem && <button className="kka-rehber-eylem" onClick={m.eylem.onClick}>{m.eylem.etiket} <i className="fas fa-arrow-right"></i></button>}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="kka-rehber-alt">
                {onceki ? (
                    <button className="kka-rehber-nav" onClick={() => onGit(onceki.key)}>
                        <i className="fas fa-arrow-left"></i> Adım {onceki.no}: {onceki.kisaAd}
                    </button>
                ) : <span />}
                {sonraki && (
                    <button className={`kka-rehber-nav ileri${sorunlu === 0 ? ' hazir' : ''}`} onClick={() => onGit(sonraki.key)}
                        title={sorunlu > 0 ? 'Bu adımda uyarılar var; yine de ilerleyebilirsiniz.' : undefined}>
                        Adım {sonraki.no}: {sonraki.kisaAd} <i className="fas fa-arrow-right"></i>
                    </button>
                )}
            </div>
        </div>
    );
};
